import React, { useEffect, useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core';
import { EntityAboutCard } from '@backstage/plugin-catalog';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';

const ALLOWED_GROUPS_ANNOTATION = 'backstage.io/allowed-groups';

const useStyles = makeStyles(() => ({
  wrapper: {
    height: '100%',
  },
  hideLaunchTemplate: {
    '& a[href*="/create/templates"]': {
      display: 'none !important',
    },
  },
}));

interface TemplateAboutCardProps {
  variant?: 'gridItem';
}

export const TemplateAboutCard = ({ variant }: TemplateAboutCardProps) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserGroups = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const groupsFromIdentity = (identity?.ownershipEntityRefs || [])
          .filter(ref => ref.startsWith('group:'))
          .map(ref => ref.toLowerCase());

        const userEntityRef = identity?.userEntityRef;
        if (!userEntityRef) {
          setUserGroups(groupsFromIdentity);
          return;
        }

        try {
          const userEntity = await catalogApi.getEntityByRef(userEntityRef);
          const groupsFromCatalog = (userEntity?.relations || [])
            .filter(relation => relation.type === 'memberOf')
            .map(relation => relation.targetRef.toLowerCase());

          setUserGroups(
            Array.from(new Set([...groupsFromIdentity, ...groupsFromCatalog])),
          );
        } catch {
          setUserGroups(groupsFromIdentity);
        }
      } catch {
        setUserGroups([]);
      } finally {
        setLoading(false);
      }
    };
    fetchUserGroups();
  }, [identityApi, catalogApi]);

  const annotations = entity.metadata.annotations || {};
  const allowedGroupsAnnotation = annotations[ALLOWED_GROUPS_ANNOTATION];

  const isAllowed = useMemo(() => {
    if (!allowedGroupsAnnotation) return true;
    if (loading) return false;

    const allowedGroups = allowedGroupsAnnotation
      .split(',')
      .map(g => g.trim().toLowerCase());

    return allowedGroups.some(group => userGroups.includes(group));
  }, [allowedGroupsAnnotation, userGroups, loading]);

  const wrapperClassName = isAllowed
    ? classes.wrapper
    : `${classes.wrapper} ${classes.hideLaunchTemplate}`;

  return (
    <div className={wrapperClassName}>
      <EntityAboutCard variant={variant} />
    </div>
  );
};
